import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { BlogSubscribeForm } from "@/components/BlogSubscribeForm";
import { usePageMeta } from "@/lib/use-page-meta";
import { getPostBySlug, blogPosts } from "@/lib/blog-posts";
import { SEO_LANDING_PAGES } from "@/lib/seo-landing-data";
import { SOCIAL_LINKS } from "@/lib/socials";
import { ArrowLeft, BookOpen, ArrowRight, Sparkles, Linkedin } from "lucide-react";

// Slugs that follow a clear "step 1, step 2..." pattern — we emit HowTo schema
// for these so they're eligible for Google's "How to" rich result.
const HOWTO_SLUGS = new Set([
  "how-to-connect-google-analytics-4-the-right-way",
  "ga4-for-small-business-owners-getting-started",
]);

// Per blog post, pick the 1 most contextually relevant SEO landing guide to
// link inline (mid-article). Keys are blog slugs; values are guide slugs from
// SEO_LANDING_PAGES. Helps Google + readers find our explainer pages.
const INLINE_GUIDE_FOR_POST = {
  "five-minute-weekly-google-analytics-review": "how-to-understand-google-analytics",
  "ga4-attribution-explained": "google-analytics-explained",
  "how-to-set-up-ga4-conversions-key-events": "ga4-for-small-business-owners",
  "ga4-metrics-that-actually-matter": "what-do-google-analytics-numbers-mean",
  "how-to-connect-google-analytics-4-the-right-way": "google-analytics-for-beginners",
  "best-google-analytics-reporting-tools-2026": "google-analytics-explained",
  "why-ga4-traffic-dropped-this-week": "how-to-understand-google-analytics",
  "how-to-read-ga4-reports-plain-english": "google-analytics-for-beginners",
  "ga4-numbers-dont-match-other-tools": "google-analytics-vs-ga4",
  "ga4-for-small-business-owners-getting-started": "ga4-for-small-business-owners",
  "whats-new-in-ga4-2026": "google-analytics-vs-ga4",
  // Iter 13 — new posts
  "what-does-engagement-rate-mean-in-google-analytics": "what-do-google-analytics-numbers-mean",
  "what-is-bounce-rate-in-ga4": "what-do-google-analytics-numbers-mean",
  "why-does-ga4-show-zero-conversions-this-week": "how-to-read-google-analytics-without-being-a-marketer",
};

// Each post also gets a 3-card "Beginner guides" footer linking to the 3 SEO
// landing pages most relevant to that post's keywords. This is the strongest
// internal-link signal (anchor text in the body + 3 contextual outbound links).
const FOOTER_GUIDES_FOR_POST = {
  "five-minute-weekly-google-analytics-review": ["how-to-understand-google-analytics", "google-analytics-for-beginners", "ga4-for-small-business-owners"],
  "ga4-attribution-explained": ["google-analytics-explained", "what-do-google-analytics-numbers-mean", "google-analytics-vs-ga4"],
  "how-to-set-up-ga4-conversions-key-events": ["ga4-for-small-business-owners", "google-analytics-for-beginners", "how-to-understand-google-analytics"],
  "ga4-metrics-that-actually-matter": ["what-do-google-analytics-numbers-mean", "google-analytics-explained", "google-analytics-for-beginners"],
  "how-to-connect-google-analytics-4-the-right-way": ["google-analytics-for-beginners", "ga4-for-small-business-owners", "how-to-understand-google-analytics"],
  "best-google-analytics-reporting-tools-2026": ["google-analytics-explained", "ga4-for-small-business-owners", "google-analytics-vs-ga4"],
  "why-ga4-traffic-dropped-this-week": ["how-to-understand-google-analytics", "what-do-google-analytics-numbers-mean", "google-analytics-explained"],
  "how-to-read-ga4-reports-plain-english": ["google-analytics-for-beginners", "what-do-google-analytics-numbers-mean", "how-to-understand-google-analytics"],
  "ga4-numbers-dont-match-other-tools": ["google-analytics-vs-ga4", "what-do-google-analytics-numbers-mean", "google-analytics-explained"],
  "ga4-for-small-business-owners-getting-started": ["ga4-for-small-business-owners", "google-analytics-for-beginners", "how-to-understand-google-analytics"],
  "whats-new-in-ga4-2026": ["google-analytics-vs-ga4", "google-analytics-explained", "google-analytics-for-beginners"],
  // Iter 13 — new posts
  "what-does-engagement-rate-mean-in-google-analytics": ["what-do-google-analytics-numbers-mean", "how-to-read-google-analytics-without-being-a-marketer", "google-analytics-explained"],
  "what-is-bounce-rate-in-ga4": ["what-do-google-analytics-numbers-mean", "google-analytics-vs-ga4", "how-to-read-google-analytics-without-being-a-marketer"],
  "why-does-ga4-show-zero-conversions-this-week": ["google-analytics-for-shopify-owners", "how-to-read-google-analytics-without-being-a-marketer", "ga4-for-small-business-owners"],
};

// Inline contextual link card — slotted between content blocks mid-article.
function InlineGuideLink({ slug }) {
  const guide = SEO_LANDING_PAGES[slug];
  if (!guide) return null;
  return (
    <Link
      to={`/${slug}`}
      className="block my-8 rounded-2xl bg-[#F7F5F0] border-l-4 border-[#E27D60] p-5 hover:bg-[#EEEBE0] transition-colors group"
      data-testid={`blog-inline-guide-${slug}`}
    >
      <div className="flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-[#E27D60] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-[#E27D60] font-semibold mb-1">
            New here? Start with the plain-English guide
          </div>
          <div className="font-display text-lg text-[#1E392A] group-hover:text-[#a8492a] transition-colors leading-snug">
            {guide.h1}
          </div>
          <div className="text-sm text-[#1E392A]/65 mt-1.5">
            {guide.description}
          </div>
          <div className="inline-flex items-center gap-1 text-xs font-medium text-[#E27D60] mt-2.5">
            Read the guide <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function buildHowToSteps(post) {
  // Pull H2 blocks that look like steps. Accept "1. xxx", "Step 1: xxx", "Step 1 — xxx".
  const STEP_RE = /^(?:step\s*)?(\d+)\s*[:.\-—]/i;
  const steps = [];
  for (let i = 0; i < post.content.length; i++) {
    const b = post.content[i];
    if (b.type === "h2" && STEP_RE.test(b.text || "")) {
      let nextParagraph = null;
      for (let j = i + 1; j < post.content.length; j++) {
        if (post.content[j].type === "p") {
          nextParagraph = post.content[j].text;
          break;
        }
      }
      steps.push({
        "@type": "HowToStep",
        name: b.text.replace(STEP_RE, "").replace(/^[\s:.\-—]+/, "").trim(),
        text: (nextParagraph || "").slice(0, 400),
      });
    }
  }
  return steps;
}

function wordCountOf(post) {
  const text = (post.content || [])
    .map((b) => {
      if (typeof b === "string") return b;
      if (b.text) return b.text;
      if (b.items) return b.items.map((i) => `${i.label || ""} ${i.body || ""}`).join(" ");
      return "";
    })
    .join(" ");
  return text.split(/\s+/).filter(Boolean).length;
}

function relatedPosts(currentSlug, all, n = 3) {
  // Simple keyword overlap; falls back to most-recent.
  const current = all.find((p) => p.slug === currentSlug);
  if (!current) return [];
  const myKw = new Set((current.keywords || []).map((k) => k.toLowerCase()));
  const others = all.filter((p) => p.slug !== currentSlug);
  others.forEach((p) => {
    const overlap = (p.keywords || []).filter((k) => myKw.has(k.toLowerCase())).length;
    p._score = overlap;
  });
  return others
    .sort((a, b) => (b._score - a._score) || b.date.localeCompare(a.date))
    .slice(0, n);
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  usePageMeta({
    title: post ? post.title : "Post not found",
    description: post ? post.description : "This post couldn't be found on Plainly.",
    path: `/blog/${slug}`,
    article: post
      ? {
          publishedTime: new Date(post.date).toISOString(),
          modifiedTime: new Date(post.date).toISOString(),
          author: post.author?.name || "Plainly",
          section: "Analytics",
          tags: post.keywords || [],
        }
      : null,
  });

  // Inject Article + BreadcrumbList (+ optional HowTo) JSON-LD for SEO
  useEffect(() => {
    if (!post) return;
    const url = `https://myplainly.com/blog/${post.slug}`;
    const publishedISO = new Date(post.date).toISOString();

    const graph = [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        headline: post.title,
        description: post.description,
        datePublished: publishedISO,
        dateModified: publishedISO,
        wordCount: wordCountOf(post),
        author: post.author
          ? {
              "@type": "Person",
              name: post.author.name,
              jobTitle: post.author.role,
              url: "https://myplainly.com/about",
              sameAs: [SOCIAL_LINKS.linkedin.url],
            }
          : { "@type": "Organization", name: "Plainly" },
        publisher: {
          "@type": "Organization",
          name: "Plainly",
          url: "https://myplainly.com",
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        inLanguage: "en-US",
        keywords: (post.keywords || []).join(", "),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://myplainly.com/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://myplainly.com/blog" },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      },
    ];

    if (HOWTO_SLUGS.has(post.slug)) {
      const steps = buildHowToSteps(post);
      if (steps.length >= 2) {
        graph.push({
          "@type": "HowTo",
          name: post.title,
          description: post.description,
          step: steps,
        });
      }
    }

    // Speakable schema — tells Google Assistant / voice search which CSS
    // selectors to read aloud when answering a voice query from this page.
    // The H1 and the lead paragraph are the natural "speak this" target.
    graph.push({
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: post.title,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "[data-speakable]"],
      },
    });

    const ld = { "@context": "https://schema.org", "@graph": graph };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-post-jsonld";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] text-[#1E392A]">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 md:px-8 py-20 text-center">
          <h1 className="font-display text-4xl">Post not found</h1>
          <p className="mt-3 text-[#1E392A]/70">We couldn&apos;t find that post.</p>
          <button
            onClick={() => navigate("/blog")}
            className="mt-6 inline-flex items-center gap-2 text-[#E27D60] underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all posts
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1E392A]">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-[#1E392A]/65 hover:text-[#1E392A]"
          data-testid="blog-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>

        <article className="mt-6">
          <div className="flex items-center gap-2 text-xs text-[#1E392A]/55">
            <BookOpen className="w-3.5 h-3.5" />
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </time>
            <span>·</span>
            <span>{post.readMinutes} min read</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-[1.1]">{post.title}</h1>
          <p
            className="mt-4 text-lg text-[#1E392A]/75 leading-relaxed"
            data-speakable="true"
          >
            {post.description}
          </p>

          {post.author ? (
            <div className="mt-6 flex items-center gap-3 pb-6 border-b border-black/10">
              <div
                className="w-10 h-10 rounded-full grid place-items-center text-sm font-display text-white shrink-0"
                style={{ backgroundColor: "#1E392A" }}
                aria-hidden="true"
              >
                {post.author.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
              </div>
              <div className="text-sm">
                <div className="font-medium text-[#1E392A] flex items-center gap-2">
                  By <Link to="/about" className="underline-offset-2 hover:underline">{post.author.name}</Link>
                  <a
                    href={SOCIAL_LINKS.linkedin.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${post.author.name} on LinkedIn`}
                    className="text-[#1E392A]/55 hover:text-[#E27D60] transition-colors"
                    data-testid="blog-author-linkedin-link"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                </div>
                <div className="text-[#1E392A]/55">{post.author.role}</div>
              </div>
            </div>
          ) : null}

          <div className="prose-plainly mt-10 space-y-5">
            {(() => {
              const inlineSlug = INLINE_GUIDE_FOR_POST[post.slug];
              const blocks = post.content.map((block, i) => (
                <ContentBlock key={`b-${i}`} block={block} />
              ));
              // Insert the contextual guide card after the 3rd content block
              // so it sits after the intro paragraphs but well above the
              // conclusion. Skip if the post is too short to support it.
              if (inlineSlug && post.content.length > 5) {
                blocks.splice(3, 0, <InlineGuideLink key="inline-guide" slug={inlineSlug} />);
              }
              return blocks;
            })()}
          </div>

          {/* Related posts — keyword-overlap ranking with most-recent fallback */}
          {(() => {
            const related = relatedPosts(post.slug, blogPosts, 3);
            if (related.length === 0) return null;
            return (
              <section className="mt-14 pt-10 border-t border-black/10" data-testid="related-posts">
                <div className="tag-label text-[#E27D60] mb-4">Keep reading</div>
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {related.map((p) => (
                    <li key={p.slug}>
                      <Link
                        to={`/blog/${p.slug}`}
                        className="block h-full rounded-2xl bg-white border border-black/5 p-5 hover:border-[#E27D60]/40 transition-colors group"
                        data-testid={`related-post-${p.slug}`}
                      >
                        <div className="font-display text-lg leading-snug group-hover:text-[#E27D60] transition-colors">
                          {p.title}
                        </div>
                        <div className="mt-2 text-xs text-[#1E392A]/55">{p.readMinutes} min read</div>
                        <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#E27D60]">
                          Read post <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })()}

          {/* Plain-English guides — internal links to our SEO landing pages so
              Google sees these guides as anchored, important pages on the site. */}
          {(() => {
            const slugs = FOOTER_GUIDES_FOR_POST[post.slug];
            if (!slugs || slugs.length === 0) return null;
            return (
              <section className="mt-10" data-testid="footer-seo-guides">
                <div className="tag-label text-[#E27D60] mb-4 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Plain-English guides
                </div>
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {slugs.map((s) => {
                    const g = SEO_LANDING_PAGES[s];
                    if (!g) return null;
                    return (
                      <li key={s}>
                        <Link
                          to={`/${s}`}
                          className="block h-full rounded-2xl bg-[#1E392A]/[0.03] border border-[#1E392A]/10 p-5 hover:bg-white hover:border-[#E27D60]/40 transition-all group"
                          data-testid={`footer-guide-${s}`}
                        >
                          <div className="font-display text-base leading-snug group-hover:text-[#a8492a] transition-colors text-[#1E392A]">
                            {g.h1}
                          </div>
                          <div className="mt-2 text-xs text-[#1E392A]/60 line-clamp-2">
                            {g.description}
                          </div>
                          <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#E27D60]">
                            Open guide <ArrowRight className="w-3.5 h-3.5" />
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })()}

          <div className="mt-14 rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-7 md:p-9">
            <div className="tag-label text-[#E27D60]">Try Plainly</div>
            <h3 className="font-display text-2xl md:text-3xl mt-2">
              Read your GA4 in two paragraphs, not two hours.
            </h3>
            <p className="mt-3 text-[#F7F5F0]/80 leading-relaxed">
              We do exactly what this post describes — automatically, every Monday morning.
              Try the demo for free, no credit card.
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#E27D60] hover:bg-[#d76d50] text-white font-medium px-5 h-10"
              data-testid="blog-cta-try-plainly"
            >
              Try the demo
            </Link>
          </div>

          <div className="mt-8">
            <BlogSubscribeForm source={`blog-post:${post.slug}`} />
          </div>
        </article>
      </main>
    </div>
  );
}

function ContentBlock({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="font-display text-2xl md:text-3xl mt-12 mb-2">{block.text}</h2>;
    case "h3":
      return <h3 className="font-display text-xl md:text-2xl mt-8 mb-1">{block.text}</h3>;
    case "p":
      return <p className="text-[#1E392A]/85 leading-relaxed">{block.text}</p>;    case "quote":
      return (
        <blockquote className="border-l-4 border-[#E27D60] pl-5 italic text-[#1E392A]/85 my-6">
          {block.text}
        </blockquote>
      );
    case "ul":
      return (
        <ul className="space-y-3 ml-0 pl-0 list-none">
          {block.items.map((item, i) => (
            <li key={i} className="pl-5 relative">
              <span className="absolute left-0 top-2.5 w-1.5 h-1.5 rounded-full bg-[#E27D60]" />
              {typeof item === "string" ? (
                <span>{item}</span>
              ) : (
                <>
                  <b className="font-medium">{item.label}.</b>{" "}
                  <span className="text-[#1E392A]/85">{item.body}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}
